import * as React from "react";
import { MouseEvent, MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { BugsnagUtil } from "ts/util/BugsnagUtil";

/** スライド表示props */
interface SlideViewProps {
  /** 画像URLリスト */
  imageUrlList: Array<string>;
}

/** HTML要素 ベンダープレフィックス拡張 */
interface HTMLElementExtVenderPrefix extends HTMLElement {
  /** requestFullscreenイベント IE */
  msRequestFullscreen?(): void;
  /** requestFullscreenイベント Chrome, Safari */
  webkitRequestFullscreen?(): void;
  /** requestFullscreenイベント Firefox */
  mozRequestFullscreen?(): void;
}

/** ドキュメント ベンダープレフィックス拡張 */
interface DocumentExtVenderPrefix extends Document {
  /** HTML要素 */
  documentElement: HTMLElementExtVenderPrefix;
  /** exitFullscreenイベント IE */
  msExitFullscreen?(): void;
  /** exitFullscreenイベント Chrome, Safari */
  webkitCancelFullScreen?(): void;
  /** exitFullscreenイベント Firefox */
  mozExitFullscreen?(): void;
  /** 全画面要素 IE */
  msFullscreenElement?: Element;
  /** 全画面要素 Chrome, Safari */
  webkitFullscreenElement?: Element;
  /** 全画面表示可否 Firefox */
  mozFullscreenElement?: Element;
  /** 全画面表示可否 IE */
  msFullscreenEnabled?: boolean;
  /** 全画面表示可否 Chrome, Safari */
  webkitFullscreenEnabled?: boolean;
  /** 全画面表示可否 Firefox */
  mozFullScreenEnabled?: boolean;
}

/** 倍率 */
interface Scale {
  /** 表示テキスト */
  text: string;
  /** 値 */
  value: number;
}

/** 倍率リスト */
const SCALE_LIST: Array<Scale> = [
  {
    text: "Default",
    value: 0
  },
  {
    text: "50%",
    value: 0.5
  },
  {
    text: "75%",
    value: 0.75
  },
  {
    text: "100%",
    value: 1
  },
  {
    text: "125%",
    value: 1.25
  },
  {
    text: "150%",
    value: 1.5
  },
  {
    text: "200%",
    value: 2
  }
];

/** 空文字 */
const EMPTY_STR: string = "";
/** 配列参照調整値 */
const ARRAY_INDEX_ADJUST_VALUE: number = 1;
/** indexOf不一致値 */
const INDEX_OF_NOT_MATCH_VALUE: number = -1;

/** tsx文字列 スラッシュ */
const TSX_SLASH: string = "/";
/** tsx文字列 × */
const TSX_X: string = "×";

/** スライドの現在表示位置 初期値 */
const CURRENT_SLIDE_NUM_INIT: number = 1;
/** 倍率 初期値 */
const SCALE_INIT: number = 0;
/** 画像サイズ 初期値 */
const IMG_SIZE_INIT: number = 0;

/** スライドの現在表示位置 1枚目 */
const CURRENT_SLIDE_FIRST_NUM: number = 1;
/** スライドの現在表示位置 変更枚数 */
const CURRENT_SLIDE_CHANGE_NUM: number = 1;

/** UserAgent比較用 iPad */
const USER_AGENT_IPAD: string = "ipad";
/** UserAgent比較用 iOS Chrome */
const USER_AGENT_CRIOS: string = "crios";

/** キー押下比較用 ArrowLeft */
const KEY_ARROW_LEFT: string = "ArrowLeft";
/** キー押下比較用 Left */
const KEY_LEFT: string = "Left";
/** キー押下比較用 ArrowRight */
const KEY_ARROW_RIGHT: string = "ArrowRight";
/** キー押下比較用 Right */
const KEY_RIGHT: string = "Right";

/** CSSクラス 表示中 */
const CSS_CLASS_IS_VIEWED: string = "is-viewed";
/** CSSクラス 活性 */
const CSS_CLASS_IS_ACTIVE: string = "is-active";
/** CSSクラス 非表示 */
const CSS_CLASS_IS_HIDE: string = "is-hide";
/** CSSクラス ズームモード中 */
const CSS_CLASS_IS_ZOOMMODE: string = "is-zoommode";
/** CSSクラス ズームモード中 */
const CSS_CLASS_IS_FULLSCREEN: string = "is-fullscreen";

/** tabindex */
const TABINDEX: number = 0;
/** CSS URL設定 前方 */
const CSS_URL_PREFIX: string = "url(";
/** CSS URL設定 前方 */
const CSS_URL_SUFFIX: string = ")";
/** CSS backgroundPosition 倍率デフォルト */
const CSS_BACKGROUNG_POSITION_DEFAULT: string = "center";
/** CSS backgroundPosition 倍率指定あり */
const CSS_BACKGROUNG_POSITION_SCARING: string = "top left";
/** CSS backgroundSize 倍率デフォルト */
const CSS_BACKGROUNG_SIZE_DEFAULT: string = "contain";
/** CSS backgroundSize 倍率指定あり */
const CSS_BACKGROUNG_SIZE_SCARING: string = "cover";
/** CSS height 倍率デフォルト */
const CSS_HEIGHT_SIZE_DEFAULT: string = "100%";
/** CSS width 倍率デフォルト */
const CSS_WIDTH_SIZE_DEFAULT: string = "100%";

/**
 * 文字列に検索文字列が含まれるか判定する
 * @param str 文字列
 * @param searchStr 検索文字列
 * @return 文字列に検索文字列が含まれるか
 */
const isMatchStr: (str: string, searchStr: string) => boolean
= (str: string, searchStr: string) => str.indexOf(searchStr) !== INDEX_OF_NOT_MATCH_VALUE;

/**
 * 全画面ボタンを非表示とするか判定する
 * @return 全画面ボタンを非表示とするか
 */
const isHiddenFullscreenButton: () => boolean = () => {
  const userAgent: string = navigator.userAgent.toLowerCase();
  const isIphoneChrome: boolean = isMatchStr(userAgent, USER_AGENT_CRIOS) && !isMatchStr(userAgent, USER_AGENT_IPAD);
  if (isIphoneChrome) {
    // iPhone & Chromeの場合は非表示とする
    return true;
  }

  const documentExt: DocumentExtVenderPrefix = document;
  const fullscreenEnabled: boolean =
    Boolean(documentExt.mozFullScreenEnabled) || Boolean(documentExt.webkitFullscreenEnabled) ||
    Boolean(documentExt.msFullscreenEnabled) || Boolean(documentExt.fullscreenEnabled);
  return !fullscreenEnabled;
};

/**
 * スライド表示
 */
export const SlideView: React.FC<SlideViewProps> = ({ imageUrlList }) => {
  // スライドの現在表示位置
  const [currentSlideNum, setCurrentSlideNum] = useState<number>(CURRENT_SLIDE_NUM_INIT);
  const refCurrentSlideNum: MutableRefObject<number> = useRef<number>(CURRENT_SLIDE_NUM_INIT);

  // 画像のオリジナル幅
  const [imgWidth, setImgWidth] = useState<number>(IMG_SIZE_INIT);
  // 画像のオリジナル高さ
  const [imgHeight, setImgHeight] = useState<number>(IMG_SIZE_INIT);
  // ズームモードの倍率
  const [scale, setScale] = useState<number>(SCALE_INIT);

  /** スライド表示用スタイル */
  const [slideFieldElmStyle, setSlideFieldElmStyle] = useState<React.CSSProperties>({
    backgroundImage: `${CSS_URL_PREFIX}${imageUrlList[currentSlideNum - ARRAY_INDEX_ADJUST_VALUE]}${CSS_URL_SUFFIX}`
  });

  // 戻るボタン非活性フラグ
  const [disabledPrevButton, setDisabledPrevButton] = useState<boolean>(false);
  // 次へボタン非活性フラグ
  const [disabledNextButton, setDisabledNextButton] = useState<boolean>(false);
  // フルスクリーン表示フラグ
  const [isFullScreen, setFullScreen] = useState<boolean>(false);
  const isRefFullScreen: MutableRefObject<boolean> = useRef<boolean>(false);
  // ズーム表示フラグ
  const [isZooming, setZooming] = useState<boolean>(false);
  const isRefZooming: MutableRefObject<boolean> = useRef<boolean>(false);
  // 全画面ボタン非表示フラグ
  const [isHideFullscreenButton, setHideFullscreenButton] = useState<boolean>(false);

  /**
   * Bugsnag に通知する。
   * @param sender Bugsnag送信処理
   * @param message メッセージ
   * @param additionalTMetadata 追加タブ
   */
  const sendBugsnag: (
    sender: (e: any, ...additionalTMetadata: Array<BugsnagMetadata>) => void,
    message: string, ...additionalTMetadata: Array<BugsnagMetadata>
  ) => void = (
    sender: (e: any, ...additionalTMetadata: Array<BugsnagMetadata>) => void,
    message: string, ...additionalTMetadata: Array<BugsnagMetadata>) => {
    sender(message, ...additionalTMetadata);
  };

  /**
   * スライドの画像URLを取得して背景に設定
   */
  const setSlider: () => void = useCallback(() => {
    // 画像サイズ設定
    const imgUrl: string = imageUrlList[currentSlideNum - ARRAY_INDEX_ADJUST_VALUE];
    const imageObj: HTMLImageElement = new Image();
    imageObj.onload = () => {
      setImgWidth(imageObj.width);
      setImgHeight(imageObj.height);
    };
    imageObj.src = imgUrl;

    // スライドの最初の場合、「前へ」ボタンを非アクティブにする
    if (currentSlideNum === CURRENT_SLIDE_FIRST_NUM) {
      setDisabledPrevButton(true);
    } else {
      setDisabledPrevButton(false);
    }

    // スライドの最後の場合、「次へ」ボタンを非アクティブにする
    if (currentSlideNum === imageUrlList.length) {
      setDisabledNextButton(true);
    } else {
      setDisabledNextButton(false);
    }
  }, [currentSlideNum]);

  /**
   * 画像の拡大縮小
   */
  const scaleImage: () => void = useCallback(() => {
    const scaleWidth: number = imgWidth * scale;
    const scaleHeight: number = imgHeight * scale;

    if (scaleWidth === IMG_SIZE_INIT || scaleHeight === IMG_SIZE_INIT) {
      // Default表示の場合
      setSlideFieldElmStyle({
        backgroundImage:
          `${CSS_URL_PREFIX}${imageUrlList[currentSlideNum - ARRAY_INDEX_ADJUST_VALUE]}${CSS_URL_SUFFIX}`,
        backgroundPosition: CSS_BACKGROUNG_POSITION_DEFAULT,
        backgroundSize: CSS_BACKGROUNG_SIZE_DEFAULT,
        height: CSS_HEIGHT_SIZE_DEFAULT,
        width: CSS_WIDTH_SIZE_DEFAULT
      });
    } else {
      // 倍率が指定されている場合
      setSlideFieldElmStyle({
        backgroundImage:
          `${CSS_URL_PREFIX}${imageUrlList[currentSlideNum - ARRAY_INDEX_ADJUST_VALUE]}${CSS_URL_SUFFIX}`,
        backgroundPosition: CSS_BACKGROUNG_POSITION_SCARING,
        backgroundSize: CSS_BACKGROUNG_SIZE_SCARING,
        height: scaleHeight,
        width: scaleWidth
      });
    }
  }, [imgWidth, imgHeight, currentSlideNum, scale]);

  /**
   * スライドの現在表示位置を1つ戻す
   */
  const minusSlideCounterNum: () => void = () => {
    if (refCurrentSlideNum.current > CURRENT_SLIDE_FIRST_NUM) {
      // スライド現在表示位置が2枚目以降の場合
      setCurrentSlideNum(refCurrentSlideNum.current - CURRENT_SLIDE_CHANGE_NUM);
      refCurrentSlideNum.current = refCurrentSlideNum.current - CURRENT_SLIDE_CHANGE_NUM;
    }
  };

  /**
   * スライドの現在表示位置を1つ進める
   */
  const addSlideCounterNum: () => void = () => {
    if (refCurrentSlideNum.current < imageUrlList.length) {
      // スライド現在表示位置が最終ページ以前の場合
      setCurrentSlideNum(refCurrentSlideNum.current + CURRENT_SLIDE_CHANGE_NUM);
      refCurrentSlideNum.current = refCurrentSlideNum.current + CURRENT_SLIDE_CHANGE_NUM;
    }
  };

  /**
   * キー押下ハンドラ
   */
  const onKeydown: (e: KeyboardEvent) => void = useCallback(e => {
    if (isRefZooming.current) {
      // ズーム表示の場合
      return;
    }
    if (e.key === KEY_ARROW_LEFT || e.key === KEY_LEFT) {
      minusSlideCounterNum();
    }
    if (e.key === KEY_ARROW_RIGHT || e.key === KEY_RIGHT) {
      addSlideCounterNum();
    }
  }, [isRefZooming]);

  /**
   * 全画面表示変更イベントハンドラ
   */
  const onFullscreenchange: () => void = useCallback(() => {
    // NOTE: ボタン押下とESCキー押下どちらの場合も、全画面表示状態の変更は本関数の前に実行されている
    const documentExtVenderPrefix: DocumentExtVenderPrefix = document;
    if (!documentExtVenderPrefix.fullscreenElement && !documentExtVenderPrefix.webkitFullscreenElement
      && !documentExtVenderPrefix.msFullscreenElement) {
      // 全画面表示中ではない場合
      setFullScreen(false);
      isRefFullScreen.current = false;
    }
  }, [isRefFullScreen]);

  /** マウント処理 イベントリスナ設定 */
  useEffect(() => {
    document.addEventListener("keydown", onKeydown);
    document.addEventListener("fullscreenchange", onFullscreenchange, false);
    document.addEventListener("webkitfullscreenchange", onFullscreenchange, false);
    document.addEventListener("MSFullscreenChange", onFullscreenchange, false);
  }, [onKeydown, onFullscreenchange]);

  /** マウント処理 表示スライド設定 */
  useEffect(() => {
    setSlider();
    if (isHiddenFullscreenButton()) {
      // iPhoneかつSafariの場合
      setHideFullscreenButton(true);
    }
  }, [setSlider]);

  /**
   * スライドの現在表示位置変更コールバック
   */
  useEffect(() => {
    setSlider();
    scaleImage();
  }, [currentSlideNum, setSlider, scaleImage]);

  /**
   * 倍率変更コールバック
   */
  useEffect(() => {
    scaleImage();
  }, [scale, scaleImage]);

  /**
   * コンテキストメニュー表示イベントハンドラ
   * @param e イベント
   */
  const onContextMenu: (e: MouseEvent<HTMLDivElement>) => void
   = (e: MouseEvent<HTMLDivElement>) => {
     e.preventDefault();
 };

  /**
   * 戻るボタン押下ハンドラ
   */
  const onClickPrev: () => void = () => {
    minusSlideCounterNum();
  };

  /**
   * 次へボタン押下ハンドラ
   */
  const onClickNext: () => void = () => {
    addSlideCounterNum();
  };

  /**
   * スライド位置オブジェクト押下ハンドラ
   * @param targetSlideNum 設定対象スライド位置
   */
  const onClickSlideItem: (targetSlideNum: number) => void = (targetSlideNum: number) => {
    setCurrentSlideNum(targetSlideNum);
    refCurrentSlideNum.current = targetSlideNum;
  };

  /**
   * ズームボタン押下ハンドラ
   */
  const onClickZoom: () => void = () => {
    setZooming(true);
    isRefZooming.current = true;
  };

  /**
   * 倍率セレクトボックスの変更ハンドラ
   */
  const onChangeScale: (e: React.ChangeEvent<HTMLSelectElement>) => void
  = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setScale(Number(e.target.value));
  };

  /**
   * ズームモード終了ボタン押下ハンドラ
   */
  const onClickZoomClose: () => void = () => {
    setScale(SCALE_INIT);
    setZooming(false);
    isRefZooming.current = false;
  };

  /**
   * 全画面表示ボタン押下ハンドラ
   */
  const onClickFullScreen: () => void = () => {
    const documentExtVenderPrefix: DocumentExtVenderPrefix = document;
    const htmlElm: HTMLElementExtVenderPrefix = document.documentElement;

    if (documentExtVenderPrefix.fullscreenElement || documentExtVenderPrefix.webkitFullscreenElement ||
      documentExtVenderPrefix.msFullscreenElement || documentExtVenderPrefix.mozFullscreenElement) {
      // フルスクリーン表示中の場合
      if (documentExtVenderPrefix.webkitCancelFullScreen) {
        // Chrome, Safariの場合
        documentExtVenderPrefix.webkitCancelFullScreen();
        setFullScreen(false);
        isRefFullScreen.current = false;
      } else if (documentExtVenderPrefix.msExitFullscreen) {
        // IEの場合
        documentExtVenderPrefix.msExitFullscreen();
        setFullScreen(false);
        isRefFullScreen.current = false;
      } else if (documentExtVenderPrefix.mozExitFullscreen) {
        // Firefoxの場合
        documentExtVenderPrefix.mozExitFullscreen();
        setFullScreen(false);
        isRefFullScreen.current = false;
      } else {
        // 他ブラウザの場合
        document.exitFullscreen().then(() => {
          setFullScreen(false);
          isRefFullScreen.current = false;
        }).catch((error: Error) => {
          sendBugsnag(BugsnagUtil.error, "onClickFullScreen: EXIT_FULLSCREEN false", {
            section: "SlideView",
            value: {
              error: error.message
            }
          });
        });
      }
    } else {
      // フルスクリーン表示中ではない場合
      if (htmlElm.webkitRequestFullscreen) {
        // Chrome, Safariの場合
        htmlElm.webkitRequestFullscreen();
        setFullScreen(true);
        isRefFullScreen.current = true;
      } else if (htmlElm.msRequestFullscreen) {
        // IEの場合
        htmlElm.msRequestFullscreen();
        setFullScreen(true);
        isRefFullScreen.current = true;
      } else if (htmlElm.mozRequestFullscreen) {
        // Firefoxの場合
        htmlElm.mozRequestFullscreen();
        setFullScreen(true);
        isRefFullScreen.current = true;
      } else {
        // 他ブラウザの場合
        htmlElm.requestFullscreen().then(() => {
          setFullScreen(true);
          isRefFullScreen.current = true;
        }).catch((error: Error) => {
          sendBugsnag(BugsnagUtil.error, "onClickFullScreen: REQUEST_FULLSCREEN false", {
            section: "SlideView",
            value: {
              error: error.message
            }
          });
        });
      }
    }
  };

  return (
    <div className="slide-wraper" onContextMenu={onContextMenu}>
      <div className="slide">
        <div className={`slide-img-flame ${isZooming ? CSS_CLASS_IS_ZOOMMODE : EMPTY_STR}`}>
          <div className="slide-img" style={slideFieldElmStyle}></div>
        </div>
        <div className={isZooming ? CSS_CLASS_IS_HIDE : EMPTY_STR} >
          <div className="slide-control">
            <div className={`slide-control-prev ${!disabledPrevButton ? CSS_CLASS_IS_ACTIVE : EMPTY_STR}`}
              role="button" tabIndex={TABINDEX} aria-label="Prev" onClick={onClickPrev}></div>
            <div className={`slide-control-next ${!disabledNextButton ? CSS_CLASS_IS_ACTIVE : EMPTY_STR}`}
              role="button" tabIndex={TABINDEX} aria-label="Next" onClick={onClickNext}></div>
          </div>
          <div className="slide-menu-field">
            <div className="slide-menu">
              <div className="slide-menu-nav">
                <span className={`slide-menu-prev ${!disabledPrevButton ? CSS_CLASS_IS_ACTIVE : EMPTY_STR}`}
                  role="button" tabIndex={TABINDEX} aria-label="Prev" onClick={onClickPrev}></span>
                <span className={`slide-menu-next ${!disabledNextButton ? CSS_CLASS_IS_ACTIVE : EMPTY_STR}`}
                  role="button" tabIndex={TABINDEX} aria-label="Next" onClick={onClickNext}></span>
              </div>
              <div className="slide-menu-tools">
                <div>
                  <span>
                    {currentSlideNum}
                  </span>
                  {TSX_SLASH}
                  <span>
                    {imageUrlList.length}
                  </span>
                </div>
                <div className="zooming-icon slide-menu-zooming"
                  role="button" tabIndex={TABINDEX} aria-label="Zoom Mode" title="Zoom Mode"
                  onClick={onClickZoom}></div>
                <div
                  className={`fullscreen-icon slide-menu-fullscreen ` +
                    `${isHideFullscreenButton ? CSS_CLASS_IS_HIDE : EMPTY_STR} ` +
                    `${isFullScreen ? CSS_CLASS_IS_FULLSCREEN : EMPTY_STR}`}
                  role="button" tabIndex={TABINDEX} aria-label="Fullscreen"
                  title="Fullscreen" onClick={onClickFullScreen}></div>
              </div>
            </div>
            <div className="slide-contents">
              {imageUrlList.map((imageUrl, i) => (
                <div
                  className={`slide-slider-item ${
                    i <= currentSlideNum - ARRAY_INDEX_ADJUST_VALUE ? CSS_CLASS_IS_VIEWED : EMPTY_STR
                  }`}
                  key={imageUrl} onClick={() => { onClickSlideItem(i + 1); }} data-slideurl={imageUrl}></div>
              ))}
            </div>
          </div>
        </div>
        <div className={`zoom-menu ${!isZooming ? CSS_CLASS_IS_HIDE : EMPTY_STR}`}>
          <div>
            <img src="/img/zoom.png" className="zoomimg" alt="Zoom" />
          </div>
          <div>
            <select className="zoom-menu-select" value={scale} onChange={onChangeScale}>
              {SCALE_LIST.map(s => (
                <option key={s.value} value={s.value}>{s.text}</option>
              ))}
            </select>
          </div>
          <div
            className={`fullscreen-icon slide-menu-fullscreen ` +
              `${isHideFullscreenButton ? CSS_CLASS_IS_HIDE : EMPTY_STR} ` +
              `${isFullScreen ? CSS_CLASS_IS_FULLSCREEN : EMPTY_STR}`}
            role="button" tabIndex={TABINDEX} aria-label="Fullscreen"
            title="Fullscreen" onClick={onClickFullScreen}></div>
          <div className="zoom-menu-close"
            role="button" tabIndex={TABINDEX} aria-label="Zoom Mode End" title="Zoom Mode End"
            onClick={onClickZoomClose}>{TSX_X}</div>
        </div>
      </div>
    </div>
  );
};
